import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CONTACT_EMAIL, TICKETS_HREF } from "../lib/site";
import { BLACK, Body, Em, GOLD, GOLD_DEEP, GoldBtn, HAIR, Head, IVORY, Kicker, Ornament, SLATE, SLATE_DIM, Section, Serif, Title } from "./ms/kit";
import { useInvite } from "./ms/InviteContext";
import { HOME_PARTNERS, PartnerWall } from "./ms/Partners";
import SiteFooter from "./ms/SiteFooter";

const GoldRule = () => (
  <div aria-hidden className="absolute left-0 right-0 top-0" style={{ height: 1, background: `linear-gradient(90deg, transparent 0%, ${GOLD_DEEP} 12%, ${GOLD} 50%, ${GOLD_DEEP} 88%, transparent 100%)` }} />
);

const Intro = () => (
  <section className="relative py-32 md:py-44" style={{ background: BLACK }} data-testid="overture">
    <GoldRule />
    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
      <Kicker>Maven Skies</Kicker>
      <Title size="lg" className="mt-8 leading-[0.98]">Building the Prairie <Em>angel investor</Em> community.</Title>
      <Body className="mt-10 max-w-2xl mx-auto">
        A curated network for people who want to learn angel investing, meet exceptional founders, and build lasting relationships across the Prairie innovation ecosystem.
      </Body>
      <Ornament />
      <p className="text-[11px] tracking-[0.36em] uppercase" style={{ color: GOLD_DEEP }}>Building people · Building capital · Building the future</p>
    </div>
  </section>
);

const Opportunity = () => (
  <Section testId="opportunity">
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24 items-start">
      <div>
        <Kicker>The opportunity</Kicker>
        <Title className="mt-6">The capital is here. <Em>The connections are next.</Em></Title>
      </div>
      <Body className="md:pt-12" tone={IVORY}>
        Across the Prairies, successful professionals, founders and business leaders have the means to invest in the companies being built around them. Many have simply never been shown how, or invited into the room. Maven Skies builds that room, and we build it alongside the organizations already doing great work here.
      </Body>
    </div>
  </Section>
);

const ROOMS = [
  { n: "01", title: "Investment Readiness", subtitle: "For startup founders getting ready to raise.", body: "Bootcamps, office hours with experienced mentors, pitch coaching and a final rehearsal on the Globe Theatre stage.", to: "/program#founder", cta: "Apply" },
  { n: "02", title: "Angel Readiness", subtitle: "For those who write the first cheques.", body: "Live sessions with active investors that build from the fundamentals to real deal experience at investor selection meetings and Demo Day.", to: "/angels#readiness", cta: "Explore Angel Readiness" },
  { n: "03", title: "MavenCircle", subtitle: "For scaleup founders.", body: "A peer network for founders raising larger rounds, growing teams and planning exits, alongside entrepreneurs who have done it.", to: "/mavencircle", cta: "Explore MavenCircle" },
  { n: "04", title: "Groundwork", subtitle: "Emerging investor infrastructure.", body: "A national program, anchored on the Prairies, that takes emerging fund managers from angel investing to an institutionally backed fund.", to: "/groundwork", cta: "Explore Groundwork" },
];

const RoomRow = ({ n, title, subtitle, body, to, cta }) => (
  <Link to={to} data-testid={`room-${n}`} className="group block" style={{ borderBottom: HAIR }}>
    <div className="grid grid-cols-[60px_1fr] md:grid-cols-[100px_1fr_auto] gap-6 md:gap-12 items-center py-10 md:py-14 transition-colors duration-500 group-hover:bg-white/[0.015]">
      <div style={{ ...Serif, color: GOLD, fontSize: "clamp(2rem, 3.4vw, 3rem)", opacity: 0.85, lineHeight: 1 }}>{n}</div>
      <div>
        <h3 className="tracking-[-0.012em] leading-[1.05]" style={{ ...Serif, color: IVORY, fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>{title}</h3>
        <p className="mt-2 text-[11px] tracking-[0.28em] uppercase" style={{ color: GOLD_DEEP }}>{subtitle}</p>
        <p className="mt-4 text-[15px] leading-[1.7] max-w-2xl" style={{ color: SLATE }}>{body}</p>
        <span className="md:hidden mt-5 inline-flex items-center gap-2 text-[10.5px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>{cta} <ArrowUpRight size={13} /></span>
      </div>
      <span className="hidden md:inline-flex items-center gap-2 text-[10.5px] tracking-[0.3em] uppercase transition-transform group-hover:translate-x-1" style={{ color: GOLD }}>{cta} <ArrowUpRight size={13} /></span>
    </div>
  </Link>
);

const TheRooms = () => (
  <Section testId="the-rooms">
    <Head kicker="The rooms">Four doors. <Em>One threshold.</Em></Head>
    <div className="mt-16" style={{ borderTop: HAIR }}>{ROOMS.map((r) => <RoomRow key={r.n} {...r} />)}</div>
  </Section>
);

const TechNights = () => (
  <Section testId="tech-nights">
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24 items-start">
      <div>
        <Kicker>Tech Nights at the Globe</Kicker>
        <Title className="mt-6">Where innovation and the arts <Em>share a stage.</Em></Title>
      </div>
      <div className="md:pt-12">
        <Body>
          With the Globe Theatre as our social network partner, Tech Nights bring founders, investors, artists and ecosystem leaders together around a live production. Every ticket is donation-based, so every evening also supports the arts in Regina.
        </Body>
        <div className="mt-10"><GoldBtn href={TICKETS_HREF} testId="tech-nights-reserve">Reserve your seat</GoldBtn></div>
      </div>
    </div>
  </Section>
);

const Partners = () => (
  <Section testId="home-partners">
    <Head kicker="Our partners">Built with partners who <Em>share the work.</Em></Head>
    <PartnerWall names={HOME_PARTNERS} />
  </Section>
);

const Invitation = () => {
  const openInvite = useInvite();
  return (
    <section id="contact" className="relative py-40 md:py-52 overflow-hidden" style={{ background: BLACK }} data-testid="invitation">
      <div aria-hidden className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ top: "12%", width: 200, height: 200, background: "radial-gradient(circle at center, rgba(245,235,210,0.15) 0%, rgba(220,205,170,0.06) 30%, transparent 70%)", filter: "blur(10px)" }} />
      <div aria-hidden className="absolute left-1/2 -translate-x-1/2 rounded-full pointer-events-none" style={{ top: "17%", width: 18, height: 18, background: "radial-gradient(circle at 36% 32%, #fbf5dd 0%, #f0e7c9 50%, #d6cdac 86%, #b3a98a 100%)", boxShadow: "0 0 12px rgba(245,240,222,0.35)" }} />
      <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center" style={{ zIndex: 2, marginTop: "8vh" }}>
        <Kicker>By invitation</Kicker>
        <Title size="lg" className="mt-8 leading-[0.98]">The door is unmarked.<br /><Em>Knock softly.</Em></Title>
        <Body className="mt-10 max-w-md mx-auto">Share a few words. We&apos;ll be in touch.</Body>
        <Ornament />
        <div className="mt-2"><GoldBtn onClick={openInvite} testId="invitation-cta">Request Invitation</GoldBtn></div>
        <p className="mt-14 text-[11px] tracking-[0.32em] uppercase" style={{ color: SLATE_DIM }}>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition" data-testid="invitation-email">{CONTACT_EMAIL}</a>
        </p>
      </div>
    </section>
  );
};

export default function HomeSections() {
  return (
    <>
      <Intro />
      <Opportunity />
      <TheRooms />
      <TechNights />
      <Partners />
      <Invitation />
      <SiteFooter />
    </>
  );
}
